import { Controller } from '../utils/controller';
import debounce from 'lodash/debounce';

interface InputRadioElement extends Element {
  checked: boolean;
}

const MOUSE_SCROLL_THRESHOLD = 5;
export default class extends Controller {
  static targets = [
    'prevButton',
    'optionWrapper',
    'activeSelectionOption',
    'selectionOption',
    'nextButton',
    'wrapper'
  ];
  static requiredTargets = [];

  static values = {
    dragSnap: {
      type: Boolean,
      default: false
    },
    selectSnap: {
      type: Boolean,
      default: true
    },
    navigateSnap: {
      type: Boolean,
      default: true
    },
    dragOverlay: {
      type: Boolean,
      default: false
    },
    itemsToShow: {
      type: Number,
      default: 0
    },
    totalItems: {
      type: Number,
      default: 0
    },
    checkContentWidth: {
      type: Boolean,
      default: false
    },
    thresholdWidthSm: {
      type: Number,
      default: 0
    },
    thresholdWidthLg: {
      type: Number,
      default: 0
    }
  };

  // TARGETS
  declare readonly wrapperTarget: HTMLElement;

  declare readonly hasPrevButtonTarget: boolean;
  declare readonly prevButtonTarget: HTMLButtonElement;
  declare readonly hasNextButtonTarget: boolean;
  declare readonly nextButtonTarget: HTMLButtonElement;

  declare readonly selectionOptionTargets: HTMLInputElement[];
  declare readonly hasOptionWrapperTarget: boolean;
  declare readonly optionWrapperTargets: HTMLElement[];

  declare readonly hasActiveSelectionOptionTarget: boolean;
  declare readonly activeSelectionOptionTarget: HTMLElement;

  // VALUES
  declare readonly itemsToShowValue: number;
  declare readonly totalItemsValue: number;
  declare readonly dragSnapValue: boolean;
  declare readonly dragOverlayValue: boolean;
  declare readonly navigateSnapValue: boolean;
  declare readonly selectSnapValue: boolean;
  declare readonly checkContentWidthValue: boolean;
  declare readonly thresholdWidthSmValue: number;
  declare readonly thresholdWidthLgValue: number;

  // STATE
  private mouseMoving = false;
  private mouseScrolling = false;
  private scrollStartScrollLeft = 0;
  private scrollStartMouseClientX = 0;

  private setScrollSnapTypeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  private connected = false;

  private handleScrollDebounced: () => void;

  private scrollToTargetDebounced: typeof this.scrollToTarget;

  private handleResizeDebounced: () => void;

  private dragOverlayElement: HTMLElement | null = null;

  connect(): void {
    this.wrapperTarget.addEventListener('mousedown', this.handleMouseDown);
    this.wrapperTarget.addEventListener('scroll', this.handleScrollDebounced);

    window.addEventListener('resize', this.handleResizeDebounced);

    // handle liveview updates cleanly
    window.addEventListener('phx:update', () => {
      if (this.checkContentWidthValue) {
        this.checkContentWidth();
      }
      const atRightEnd =
        this.scrollStartScrollLeft + this.getWrapperWidth() >= this.wrapperTarget.scrollWidth;
      const behavior = atRightEnd ? 'instant' : 'smooth';
      this.scrollToCheckedRadioButton(behavior);
      this.updateButtons();
    });

    this.scrollToCheckedRadioButton('auto');
    this.updateButtons();

    if (this.checkContentWidthValue) {
      this.checkContentWidth();
    }

    this.connected = true;
  }

  disconnect(): void {
    this.wrapperTarget.removeEventListener('mousedown', this.handleMouseDown);
    this.wrapperTarget.removeEventListener('scroll', this.handleScrollDebounced);

    window.removeEventListener('resize', this.handleResizeDebounced);

    document.body.removeEventListener('mousemove', this.handleMouseMove);
    document.body.removeEventListener('mouseleave', this.handleMouseUp);
    document.body.removeEventListener('mouseup', this.handleMouseUp);

    this.dragOverlayElement?.parentElement?.removeChild(this.dragOverlayElement);
    this.dragOverlayElement = null;

    this.connected = false;
  }

  initialize(): void {
    this.handleScrollDebounced = debounce(() => {
      this.updateButtons();
    }, 100);
    this.scrollToTargetDebounced = debounce(this.scrollToTarget, 150);
    this.handleResizeDebounced = debounce(this.handleResize, 500);
  }

  checkContentWidth(): void {
    const overflowWidth = this.wrapperTarget.scrollWidth - this.wrapperTarget.clientWidth;
    const breakpoint = 768;
    const thresholdWidth =
      window.innerWidth >= breakpoint ? this.thresholdWidthLgValue : this.thresholdWidthSmValue;

    if (overflowWidth >= thresholdWidth) {
      if (this.hasPrevButtonTarget && this.hasNextButtonTarget) {
        this.prevButtonTarget.style.display = 'block';
        this.nextButtonTarget.style.display = 'block';
      }
    }
  }

  next() {
    let fallback = true;
    if (this.navigateSnapValue) {
      const target = this.computeNavigateNextTarget();
      if (target) {
        fallback = false;
        this.scrollToTarget(target);
      }
    }

    if (fallback) {
      this.wrapperTarget.scrollBy({
        left: this.wrapperTarget.offsetWidth,
        behavior: 'smooth'
      });
    }
  }

  prev() {
    let fallback = true;
    if (this.navigateSnapValue) {
      const target = this.computeNavigatePrevTarget();
      if (target) {
        fallback = false;
        this.scrollToTarget(target);
      }
    }
    if (fallback) {
      this.wrapperTarget.scrollBy({
        left: -this.wrapperTarget.offsetWidth,
        behavior: 'smooth'
      });
    }
  }

  activeSelectionOptionTargetConnected() {
    if (this.selectSnapValue && this.hasActiveSelectionOptionTarget) {
      this.scrollToTargetDebounced(this.activeSelectionOptionTarget);
    }
  }

  itemSelect(e: CustomEvent): void {
    const currentTarget = e.currentTarget;
    if (this.mouseMoving || this.mouseScrolling || !currentTarget) {
      e.preventDefault();

      return;
    }

    if (this.selectSnapValue) {
      const containingOptionWrapper = this.getContainingOptionWrapper(currentTarget as HTMLElement);
      this.scrollToTargetDebounced(containingOptionWrapper);
    }

    this.handleScrollDebounced();
  }

  handleSkuChangeEvent = (customEvent: CustomEvent<{ sku?: { id: string } }>) => {
    if (!customEvent?.detail?.sku?.id) {
      return;
    }

    this.setAndRefreshSelectedSku(customEvent.detail.sku.id);
  };

  private updateButtons = (): void => {
    const scrollPos = Math.ceil(this.wrapperTarget.scrollLeft);
    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.disabled = scrollPos === 0;
    }
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled =
        scrollPos + this.getWrapperWidth() >= this.wrapperTarget.scrollWidth;
    }
  };

  private handleResize = () => {
    this.scrollToCheckedRadioButton('auto');
    this.updateButtons();
  };

  private handleMouseDown = (e: MouseEvent): void => {
    this.mouseMoving = false;
    this.mouseScrolling = false;

    this.scrollStartMouseClientX = e.clientX;
    this.scrollStartScrollLeft = this.wrapperTarget.scrollLeft;

    // prevent scroll snap (if any)
    this.overrideScrollSnapType();

    window.addEventListener('blur', this.handleMouseUp);
    window.addEventListener('mouseup', this.handleMouseUp);
    document.body.addEventListener('mousemove', this.handleMouseMove);
  };

  private handleMouseUp = (e: MouseEvent): void => {
    e.preventDefault();

    this.mouseMoving = false;

    this.resetScrollSnapTypeDelayed();

    window.removeEventListener('blur', this.handleMouseUp);
    document.body.removeEventListener('mousemove', this.handleMouseMove);
    document.body.removeEventListener('mouseup', this.handleMouseUp);

    this.dragOverlayElement?.parentElement?.removeChild(this.dragOverlayElement);
    this.dragOverlayElement = null;

    if (this.mouseScrolling) {
      e.stopImmediatePropagation();
      e.stopPropagation();

      this.scrollSnap();
      this.resetMouseScrollingDelayed();
    }
  };

  private handleMouseMove = (e: MouseEvent) => {
    e.preventDefault();

    this.mouseMoving = true;
    this.mouseScrolling =
      this.mouseScrolling ||
      Math.abs(e.clientX - this.scrollStartMouseClientX) >= MOUSE_SCROLL_THRESHOLD;

    if (this.mouseScrolling) {
      if (this.dragOverlayValue && !this.dragOverlayElement) {
        this.dragOverlayElement = document.createElement('div');
        this.dragOverlayElement.className = 'vul-shades-horizontal-scroll__drag-overlay';
        document.body.appendChild(this.dragOverlayElement);
      }

      const distance = this.scrollStartMouseClientX - e.clientX;
      this.wrapperTarget.scrollTo({
        left: this.scrollStartScrollLeft + distance,
        behavior: 'auto'
      });
    }
  };

  private getWrapperWidth() {
    return this.wrapperTarget.offsetWidth;
  }

  private scrollSnap() {
    if (
      !this.dragSnapValue ||
      this.wrapperTarget.scrollLeft + this.getWrapperWidth() >= this.wrapperTarget.scrollWidth
    ) {
      return;
    }

    const targetItem = this.computeSnapLeftTarget();
    if (targetItem) {
      this.scrollToTargetDebounced(targetItem);
    }
  }

  private scrollToTarget(e: HTMLElement, behavior: ScrollBehavior = 'smooth') {
    const bounds = this.wrapperTarget.getBoundingClientRect();
    const targetBounds = e.getBoundingClientRect();
    const paddingLeftPx = Number.parseFloat(
      window.getComputedStyle(this.wrapperTarget).paddingLeft
    );
    const paddingLeft = Number.isFinite(paddingLeftPx) ? paddingLeftPx : 0;

    const offset = targetBounds.left - paddingLeft - bounds.left;

    this.wrapperTarget.scrollTo({
      left: this.wrapperTarget.scrollLeft + offset,
      behavior
    });
  }

  private scrollToCheckedRadioButton = (behavior: ScrollBehavior = 'smooth') => {
    for (const el of this.selectionOptionTargets) {
      if (el.checked) {
        this.scrollToTarget(this.getContainingOptionWrapper(el), behavior);
        break;
      }
    }
  };

  setAndRefreshSelectedSku(skuId: string) {
    const radioButton: InputRadioElement | null = this.element.querySelector(`#sku_id_${skuId}`);

    if (radioButton == null) {
      return;
    }

    radioButton.checked = true;
    this.scrollToCheckedRadioButton('auto');
  }

  private computeSnapLeftTarget(): HTMLElement | null {
    const bounds = this.wrapperTarget.getBoundingClientRect();
    return this.computeClosestOptionTarget(bounds.left);
  }

  private computeNavigatePrevTarget(): HTMLElement | null {
    const bounds = this.wrapperTarget.getBoundingClientRect();
    return this.computeClosestOptionTarget(bounds.left - bounds.width + 1);
  }

  private computeNavigateNextTarget(): HTMLElement | null {
    const bounds = this.wrapperTarget.getBoundingClientRect();

    return this.computeClosestOptionTarget(bounds.right + 1);
  }

  private computeClosestOptionTarget(targetX: number): HTMLElement | null {
    let result: HTMLElement | null = null;
    let resultDistance = Number.POSITIVE_INFINITY;

    for (const e of this.optionWrapperTargets) {
      const targetBounds = e.getBoundingClientRect();
      const distance = Math.min(
        Math.abs(targetBounds.left - targetX),
        Math.abs(targetBounds.right - targetX)
      );
      if (distance <= resultDistance) {
        resultDistance = distance;
        result = e;
      }
    }

    return result;
  }

  private resetMouseScrollingDelayed() {
    setTimeout(() => {
      this.mouseScrolling = false;
    }, 0);
  }

  private getContainingOptionWrapper(e: HTMLElement) {
    let target: HTMLElement | null = e;
    while (target) {
      if (
        target &&
        target.parentElement &&
        target.parentElement.getAttribute('data-horizontal-scroll-target') === 'wrapper'
      ) {
        return target;
      } else {
        target = target.parentElement;
      }
    }

    return e;
  }

  private overrideScrollSnapType() {
    if (this.setScrollSnapTypeTimeoutId) {
      clearTimeout(this.setScrollSnapTypeTimeoutId);
      this.setScrollSnapTypeTimeoutId = null;
    }
    this.wrapperTarget.style.scrollSnapType = 'none';
  }

  private resetScrollSnapTypeDelayed() {
    if (this.setScrollSnapTypeTimeoutId) {
      clearTimeout(this.setScrollSnapTypeTimeoutId);
    }
    this.setScrollSnapTypeTimeoutId = setTimeout(() => {
      // @ts-expect-error Reset style
      this.wrapperTarget.style.scrollSnapType = null;
    }, 500);
  }
}
